
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { SupabaseClient } from "@supabase/supabase-js";
import { toast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import TopicForm from "./TopicForm";

type Topic = { id: string; name: string; parent_topic_id: string | null };

// Use an untyped Supabase client here to avoid TS errors until generated types include "topics"
const sb = supabase as unknown as SupabaseClient<any>;

const fetchTopics = async (): Promise<Topic[]> => {
  console.log("Fetching topics...");
  const { data, error } = await sb
    .from("topics")
    .select("id,name,parent_topic_id")
    .order("name", { ascending: true });
  if (error) {
    console.error("Error fetching topics:", error);
    throw error;
  }
  return data ?? [];
};

const TopicManager = () => {
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["topics"],
    queryFn: fetchTopics,
  });

  const topics = data ?? [];
  const byId = useMemo(() => {
    const map = new Map<string, Topic>();
    topics.forEach((t) => map.set(t.id, t));
    return map;
  }, [topics]);

  const [editing, setEditing] = useState<Topic | null>(null);
  const [deleting, setDeleting] = useState<Topic | null>(null);
  const [query, setQuery] = useState("");
  const filtered = useMemo(
    () => topics.filter((t) => t.name.toLowerCase().includes(query.toLowerCase())),
    [topics, query]
  );

  const createMutation = useMutation({
    mutationFn: async (payload: { name: string; parent_topic_id: string | null }) => {
      console.log("Creating topic payload:", payload);
      const { error } = await sb.from("topics").insert(payload);
      if (error) throw error;
    },
  onSuccess: async () => {
    toast({ title: "Topic created" });
    await qc.invalidateQueries({ queryKey: ["topics"] });
  },
  onError: (e: any) => {
    toast({ title: "Failed to create topic", description: e?.message ?? "An error occurred", variant: "destructive" });
  },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...payload }: { id: string; name: string; parent_topic_id: string | null }) => {
      console.log("Updating topic", id, payload);
      const { error } = await sb.from("topics").update(payload).eq("id", id);
      if (error) throw error;
    },
  onSuccess: async () => {
    toast({ title: "Topic updated" });
    setEditing(null);
    await qc.invalidateQueries({ queryKey: ["topics"] });
  },
  onError: (e: any) => {
    toast({ title: "Failed to update topic", description: e?.message ?? "An error occurred", variant: "destructive" });
  },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log("Deleting topic", id);
      
      // First, update MCQs to remove topic reference
      const { error: mcqError } = await sb
        .from("mcqs")
        .update({ topic_id: null })
        .eq("topic_id", id);
      if (mcqError) throw mcqError;
      
      // Then, update notes to remove topic reference
      const { error: noteError } = await sb
        .from("notes")
        .update({ topic_id: null })
        .eq("topic_id", id);
      if (noteError) throw noteError;
      
      // Finally, delete the topic
      const { error } = await sb.from("topics").delete().eq("id", id);
      if (error) throw error;
    },
  onSuccess: async () => {
    toast({ title: "Topic deleted successfully" });
    setDeleting(null);
    await qc.invalidateQueries({ queryKey: ["topics"] });
  },
  onError: (e: any) => {
    console.error("Delete error:", e);
    toast({ title: "Failed to delete topic", description: e?.message ?? "An error occurred", variant: "destructive" });
  },
  });

  return (
    <div className="space-y-6 animate-fade-in">
      <Card>
        <CardHeader>
          <CardTitle>Add new topic</CardTitle>
        </CardHeader>
        <CardContent>
          <TopicForm
            topics={topics}
            submitting={createMutation.isPending}
            onSubmit={(payload) => createMutation.mutate(payload)}
            submitLabel="Create"
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Existing topics</CardTitle>
          <div className="w-full sm:w-72">
            <Input placeholder="Search topics" value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-9 w-full" />
              ))}
            </div>
          ) : error ? (
            <div className="text-destructive">Failed to load topics.</div>
          ) : topics.length === 0 ? (
            <div className="text-muted-foreground">No topics yet.</div>
          ) : filtered.length === 0 ? (
            <div className="text-muted-foreground">No matching topics.</div>
          ) : (
            <div className="relative w-full overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40%] sticky top-0 bg-background z-10">Name</TableHead>
                    <TableHead className="w-[40%] sticky top-0 bg-background z-10">Parent</TableHead>
                    <TableHead className="w-[20%] sticky top-0 bg-background z-10 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {filtered.map((t) => {
                  const parent = t.parent_topic_id ? byId.get(t.parent_topic_id) : null;
                    return (
                      <TableRow key={t.id}>
                        <TableCell>{t.name}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {parent ? parent.name : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="hover-scale">Actions</Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="z-50 bg-popover">
                              <DropdownMenuItem onClick={() => setEditing(t)}>Rename / Reparent</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-destructive focus:text-destructive" 
                                onClick={() => setDeleting(t)}
                              >
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
)}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit topic</DialogTitle>
          </DialogHeader>
          {editing && (
            <TopicForm
              topics={topics}
              initialName={editing.name}
              initialParentId={editing.parent_topic_id}
              submitting={updateMutation.isPending}
              onSubmit={(payload) => updateMutation.mutate({ id: editing.id, ...payload })}
              onCancel={() => setEditing(null)}
              submitLabel="Update"
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete topic?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete notes under this topic and detach any subtopics. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => deleting && deleteMutation.mutate(deleting.id)} 
              disabled={deleteMutation.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default TopicManager;
